import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';
import { hasAdminTab } from './auth/roles.js';
import { setTokenProvider, setSpTokenProvider, setApiTokenProvider } from './api/client.js';
import { setImpersonationProvider, setReauthHandler } from './api/apiFetch.js';
import { AppConfigProvider } from './contexts/AppConfigContext.jsx';
import { PinLockProvider, usePinLock } from './contexts/PinLockContext.jsx';
import PinLockOverlay from './components/PinLockOverlay.jsx';
import SubIdentityGate from './auth/SubIdentityGate.jsx';
import BootStage from './components/BootStage.jsx';
import Layout from './components/Layout.jsx';
import Dashboard from './pages/Dashboard.jsx';
import CaseList from './pages/CaseList.jsx';
import CaseDetail from './pages/CaseDetail.jsx';
import NewCaseWizard from './pages/NewCaseWizard.jsx';
import AdminPanel from './pages/AdminPanel.jsx';
import NotificationRuleWizard from './pages/NotificationRuleWizard.jsx';
import UIBook from './pages/UIBook.jsx';
import Knowledge from './pages/Knowledge.jsx';
import ClinicApprovals from './pages/ClinicApprovals.jsx';
import { ClinicApprovalsProvider } from './contexts/ClinicApprovalsContext.jsx';
import Monitor from './pages/Monitor/Monitor.jsx';
import './App.css';

function App() {
  const { user, getToken, getApiToken, getSpToken, getImpersonation, triggerReauth } = useAuth();

  // Register synchronously in the render body — not in useEffect — so the
  // token is available before any child component mounts and calls the API.
  if (getToken)    setTokenProvider(getToken);
  if (getApiToken) setApiTokenProvider(getApiToken);
  if (getSpToken)  setSpTokenProvider(getSpToken);
  if (getImpersonation) setImpersonationProvider(getImpersonation);
  if (triggerReauth) setReauthHandler(triggerReauth);

  return (
    <BootStage id="app.shell" label="Application shell">
    <AppConfigProvider>
    <BootStage id="app.config" label="Configuration">
    <PinLockProvider>
    <BootStage id="app.pin" label="Session PIN">
    <SubIdentityGate>
    <ClinicApprovalsProvider>
    <Layout>
      <PinLockGate />
      <Routes>
        {/* Root redirects to dashboard */}
        <Route path="/" element={<Navigate to="/cases" replace />} />

        {/* Stats dashboard — its own role capability since the reportView split. */}
        <Route path="/dashboard" element={
          user?.canSeeDashboard
            ? <BootStage id="route.dashboard" label="Dashboard"><Dashboard /></BootStage>
            : <Navigate to="/cases" replace />
        } />

        {/* All cases list */}
        <Route path="/cases" element={
          <BootStage id="route.cases" label="All Cases"><CaseList /></BootStage>
        } />

        {/* Case detail view — /cases/:id where id is the SharePoint item ID */}
        <Route path="/cases/:id" element={
          <BootStage id="route.case-detail" label="Case detail"><CaseDetail /></BootStage>
        } />

        {/* Knowledge — in-app user guide, per-role toggle in Admin ▸ Roles;
            articles additionally filter per tier inside the page (knowledge_logic.md). */}
        <Route path="/knowledge" element={
          user?.canSeeKnowledge
            ? <BootStage id="route.knowledge" label="Knowledge"><Knowledge /></BootStage>
            : <Navigate to="/cases" replace />
        } />
        <Route path="/knowledge/:articleId" element={
          user?.canSeeKnowledge
            ? <BootStage id="route.knowledge" label="Knowledge"><Knowledge /></BootStage>
            : <Navigate to="/cases" replace />
        } />

        {/* Notifications — clinic-access approvals inbox (Supervisor tier and up). */}
        <Route path="/notifications" element={
          user?.canManageClinicAccess
            ? <BootStage id="route.notifications" label="Notifications"><ClinicApprovals /></BootStage>
            : <Navigate to="/cases" replace />
        } />

        {/* New case form — only roles that can write cases. */}
        <Route
          path="/new-case"
          element={
            user?.canSubmitCase
              ? <BootStage id="route.new-case" label="New case"><NewCaseWizard /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Admin panel — SuperAdmin (full) or a scoped tab subset (Supervisor). */}
        <Route
          path="/admin"
          element={
            user?.adminTabs
              ? <BootStage id="route.admin" label="Admin panel"><AdminPanel /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Notification rule wizard — create/edit one custom rule. Gated
            exactly like the Admin ▸ Notifications tab (SuperAdmin only). */}
        <Route
          path="/admin/notifications/rules/new"
          element={
            hasAdminTab(user, 'Notifications')
              ? <BootStage id="route.notif-rule" label="Notification rule"><NotificationRuleWizard /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />
        <Route
          path="/admin/notifications/rules/:id"
          element={
            hasAdminTab(user, 'Notifications')
              ? <BootStage id="route.notif-rule" label="Notification rule"><NotificationRuleWizard /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* UI Book — design-system reference, SuperAdmin only */}
        <Route
          path="/ui-book"
          element={
            user?.canManageAdminConfig
              ? <BootStage id="route.ui-book" label="UI Book"><UIBook /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Ops Monitor — SuperAdmin only */}
        <Route
          path="/monitor"
          element={
            user?.isSuperAdmin
              ? <BootStage id="route.monitor" label="Ops Monitor"><Monitor /></BootStage>
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Catch-all — redirect unknown routes to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
    </ClinicApprovalsProvider>
    </SubIdentityGate>
    </BootStage>
    </PinLockProvider>
    </BootStage>
    </AppConfigProvider>
    </BootStage>
  );
}

// Renders the lock/setup overlay when the PinLockContext is in a non-unlocked
// stage. Kept inside the provider so it can subscribe to state changes.
function PinLockGate() {
  const lock = usePinLock();
  if (!lock || lock.stage === 'unlocked' || lock.stage === 'loading') return null;
  return <PinLockOverlay />;
}

export default App;
